import React, { memo, ChangeEvent, useMemo, useCallback } from 'react'
import { Select, ISelectOption } from 'components/forms/Selects'
import { RadioGroup } from 'transfix-ui/components/RadioGroup'
import { Grid, Box } from 'transfix-ui/core'
import { $enum } from 'ts-enum-util'
import { EQUIPMENT_TYPE, TEMPERATURE_TYPE, TRAILER_TYPES } from 'apps/fleet-planner/constants/loads'
import { ILoadEquipment, ITemperatureSetting } from 'apps/fleet-planner/types/loads'
import { TextInput } from 'components/atoms'

export interface IEquipmentProps {
  trailerType?: EQUIPMENT_TYPE
  trailerLength?: string
  temperatureSetting?: ITemperatureSetting
  /**
   * @default true
   */
  large?: boolean
  onChange: (change: Partial<ILoadEquipment>) => void
}

const equipmentTypes = $enum(EQUIPMENT_TYPE).map((value, label) => ({ label, value }))

const temperatureTypes = [
  {
    value: TEMPERATURE_TYPE.fixed,
    label: 'Fixed Temperature',
    dataTest: 'fixed-temp',
    id: `temperatureType-${TEMPERATURE_TYPE.fixed}`,
  },
  {
    value: TEMPERATURE_TYPE.continuous,
    label: 'Continuous Flow',
    dataTest: 'cont-flow',
    id: `temperatureType-${TEMPERATURE_TYPE.continuous}`,
  },
]

/**
 * All trailers have `53` as default, but `Specialized` should have no value
 */
const getDefaultTrailerLength = (trailerType: EQUIPMENT_TYPE): string | undefined => {
  if (trailerType === EQUIPMENT_TYPE.Specialized) {
    return undefined
  }

  return '53'
}

export const Equipment = memo<IEquipmentProps>(
  ({ large = true, onChange, temperatureSetting, trailerLength, trailerType }) => {
    const { fixedTemperature, maxTemperature, minTemperature, precool, temperatureType } =
      temperatureSetting || {}

    const initialTemperatureSetting = {
      temperatureType: undefined,
      precool: '',
      minTemperature: '',
      maxTemperature: '',
      fixedTemperature: '',
    }

    const prevTemp = { temperatureType, precool, minTemperature, maxTemperature, fixedTemperature }
    const equipmentTrailers = trailerType ? TRAILER_TYPES[trailerType] : []
    const selectedEquipmentType =
      equipmentTypes.find(equipmentTypeOption => equipmentTypeOption.value === trailerType) ||
      equipmentTypes[0]

    const trailerTypeOptions = useMemo(
      () =>
        equipmentTrailers.map((value: string | number) => ({
          label: `${value}${Number(value) ? "'" : ''} ${selectedEquipmentType.label}`,
          value: `${value}`,
        })),
      [equipmentTrailers, selectedEquipmentType.label]
    )

    const equipmentTypeChangeHandler = useCallback(
      (e: ISelectOption<EQUIPMENT_TYPE>) => {
        const defaultTrailerLength = getDefaultTrailerLength(e.value)
        onChange({
          trailerType: e.value,
          trailerLength: defaultTrailerLength,
          ...(temperatureType ? { temperatureSetting: initialTemperatureSetting } : {}),
        })
      },
      [onChange, initialTemperatureSetting, temperatureType]
    )

    const trailerLengthChangeHandler = useCallback(
      (e: ISelectOption<string>) => {
        onChange({ trailerLength: e.value })
      },
      [onChange]
    )

    const temperatureTypeOptionChangeHandler = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const updatedTempType = e.target.value as TEMPERATURE_TYPE
        onChange({ temperatureSetting: { ...prevTemp, temperatureType: updatedTempType } })
      },
      [onChange, prevTemp]
    )

    const temperatureChangeHandler = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        onChange({ temperatureSetting: { ...prevTemp, [e.target.id]: e.target.value } })
      },
      [onChange, prevTemp]
    )

    const fixedTemp = temperatureType === TEMPERATURE_TYPE.fixed

    return (
      <>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Select
              label='Equipment Type'
              options={equipmentTypes}
              value={trailerType}
              placeholder='Select Equipment Type'
              onChange={equipmentTypeChangeHandler}
              dataTest='equipment-select'
              large={large}
            />
          </Grid>
          {trailerTypeOptions.length > 0 && (
            <Grid item md={6} xs={12}>
              <Select
                label='Trailer Type'
                placeholder='Select Trailer Type'
                options={trailerTypeOptions}
                value={trailerLength}
                onChange={trailerLengthChangeHandler}
                dataTest='trailer-select'
                large={large}
              />
            </Grid>
          )}
        </Grid>
        {trailerType === EQUIPMENT_TYPE.Reefer && (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  <strong>Temperature Settings</strong>
                </Box>
                <RadioGroup
                  label=''
                  name='temperatureType'
                  required={false}
                  onOptionChange={temperatureTypeOptionChangeHandler}
                  checked={temperatureType}
                  options={temperatureTypes}
                />
              </Grid>
              <Grid item md={3} xs={12}>
                <TextInput
                  placeholder='00'
                  label='Precool'
                  type='number'
                  id='precool'
                  value={precool || 0}
                  onChange={temperatureChangeHandler}
                  dataTest='precool'
                  large={large}
                />
              </Grid>
              {fixedTemp ? (
                <Grid item xs={12} md={3}>
                  <TextInput
                    placeholder='00'
                    label='Temp'
                    type='number'
                    value={fixedTemperature || 0}
                    onChange={temperatureChangeHandler}
                    id='fixedTemperature'
                    dataTest='temp'
                    large={large}
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} md={3}>
                    <TextInput
                      large={large}
                      placeholder='00'
                      label='Min'
                      type='number'
                      id='minTemperature'
                      value={minTemperature || 0}
                      onChange={temperatureChangeHandler}
                      dataTest='min'
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextInput
                      large={large}
                      placeholder='00'
                      label='Max'
                      type='number'
                      id='maxTemperature'
                      value={maxTemperature || 0}
                      onChange={temperatureChangeHandler}
                      dataTest='max'
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </>
        )}
      </>
    )
  }
)
